import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { getHub } from '@/services/signalr';
import { useToast } from './toast';
import { useConfirm } from './confirm';
import { selectiveAlarm } from '@/services/selectiveAlarm';
import { onNativeSelectiveResponse, notifyNativeSelectiveResponse, isInNativeWrapper, checkNativeGpsEnabled, openNativeGpsSettings, onNativeGpsTrackingStatus } from '@/services/nativeBridge';
import { gpsTracking } from '@/services/gpsTracking';
import { api } from '@/services/api';
import { useAuth } from './AuthContext';
import { useSelectiveResponseHandler } from '@/features/selectives/useSelectiveResponseHandler';
import { SelectiveMultiModal, type PendingSelective } from './SelectiveMultiModal';
import { SelectiveEndChimeModal } from './SelectiveEndChimeModal';

/**
 * Listener globale degli eventi selettive lato PWA. Monta i listener su
 * SignalR /hubs/selective + sui CustomEvent del wrapper nativo, e gestisce
 * audio/modal nel browser puro. Deve essere montato UNA volta nel layout.
 *
 * State machine (4 percorsi):
 *
 *   selectiveCreated + web puro (no wrapper)
 *     -> targetUserIds check -> audio start + toast + Notification API + invalidate query
 *
 *   selectiveCreated + dentro wrapper nativo
 *     -> SOLO invalidate query (audio + modal sono gestiti dall'hook
 *        useFcmListeners del wrapper). Niente audio doppio.
 *
 *   selectiveNoLongerNeeded + web puro ("intervento non piu' necessario")
 *     -> SUONA endChime + modal "Ho ricevuto" se ero fra acceptedUserIds +
 *        Notification API. La selettiva RESTA attiva (non rimossa dal pending).
 *
 *   selectiveNoLongerNeeded + dentro wrapper nativo
 *     -> SOLO invalidate query (audio end-chime + modal gestiti dal wrapper).
 *
 *   selectiveTerminated + web puro (cleanup SILENZIOSO)
 *     -> rimuove dal pending + stop sirena + stop GPS. NESSUN suono end-chime.
 *
 *   selectiveTerminated + dentro wrapper nativo
 *     -> SOLO invalidate query (cleanup GPS gestito dal wrapper).
 *
 *   vvf:native:accept / vvf:native:reject (CustomEvent dal wrapper)
 *     -> POST /respond con GPS (entrambi i contesti) + gpsTracking.start
 *        + stop sirena (solo PWA pura, in wrapper la ferma il wrapper).
 */
export function GlobalSelectiveAlert(): React.JSX.Element | null {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // useConfirm: serve per gestire 409 AlreadyActiveOnAnother quando l'utente
  // accetta una seconda selettiva dal modal multi-sel mentre e' gia' attivo
  // su un'altra. Chiediamo conferma allo switch (rifiuta vecchia + accetta
  // nuova) come fa SelectivesModals.tryAccept.
  const confirm = useConfirm();
  // Audit techlead #4: useAuth invece di letture dirette localStorage.
  // Cosi' bypassVolume aggiornato in Settings, role/approved cambiati dal
  // backend etc. si propagano automaticamente all'handler senza reload.
  const { user } = useAuth();
  // Hook condiviso per la POST /respond — stessa logica di ActiveSelectiveItem
  // (GPS, 409 confirm switch, toast, invalidate queries).
  const responseHandler = useSelectiveResponseHandler();
  // Coda end-chime: ogni selectiveTerminated viene appesa. Il modal mostra
  // la prima della coda; "Ho ricevuto" la rimuove. Prima era un boolean
  // singolo, ma se due selettive terminavano in rapida successione la
  // seconda sovrascriveva la prima e l'ack andava perso.
  const [pendingEndChimes, setPendingEndChimes] = useState<string[]>([]);
  // Audit techlead #7 — Modal multi-sel PWA: array pending + frecce.
  // Allineato al wrapper RN: il modal mostra tutte le selettive di cui
  // l'utente e' target e a cui non ha ancora risposto, e si chiude solo
  // quando la lista e' vuota. Solo per browser puro (no wrapper).
  const [pending, setPending] = useState<PendingSelective[]>([]);
  const [pendingIndex, setPendingIndex] = useState(0);

  useEffect(() => {
    const conn = getHub('/api/hubs/selective');

    // P4 audio ownership unificata: selectiveAlarm.* delega automaticamente
    // al wrapper RN se isInNativeWrapper, oppure suona via HTMLAudio se
    // browser puro. Niente piu' guard inWrapper sull'audio qui — la
    // funzione e' sempre safe da chiamare. Restano i guard solo per i
    // MODAL (modal endChime PWA va saltato in wrapper perche' c'e' gia'
    // l'EndChimeModal nativo). Toast e Notification API girano sempre
    // anche dentro wrapper (la WebView li mostra al di sopra del nativo).
    const inWrapper = isInNativeWrapper();

    // Fetch della lista pending dal backend (allineato al pattern wrapper RN).
    // Idempotente: chiamate concorrenti coalescono nella stessa fetch.
    let fetching = false;
    const refreshPending = async () => {
      if (inWrapper) return; // in wrapper il modal e' nativo, non PWA
      if (fetching) return;
      fetching = true;
      try {
        const res = await api.get<PendingSelective[]>('/selectives/active-for-me');
        setPending(res.data ?? []);
      } catch {
        // ignore — la prossima selectiveCreated rifetcheraà
      } finally {
        fetching = false;
      }
    };

    // NB: nel payload SignalR `type` e' il DISCRIMINATOR ('selective.created'),
    // il tipo intervento ("Incendio", "Soccorso"...) e' in `selectiveType`
    // (allineato a SelectiveCreatedPayload). Usare `type` per il nome
    // dell'intervento mostrerebbe 'selective.created' all'utente — bug fix.
    const handleSelectiveCreated = (selective: { type?: string; selectiveType?: string; priority?: string; zone?: string; targetUserIds?: string[] }) => {
      qc.invalidateQueries({ queryKey: ['selectives.active'] });
      qc.invalidateQueries({ queryKey: ['dashboard.summary'] });
      // Problema 4: se backend ha incluso targetUserIds e l'utente loggato
      // non e' tra i target, non far partire audio ne' modal (la selettiva
      // compare comunque in lista grazie all'invalidate sopra).
      const targets = selective?.targetUserIds;
      if (Array.isArray(targets) && targets.length > 0 && user?.id && !targets.includes(user.id)) {
        return;
      }
      // Modal nativo + audio nel wrapper: lui prende il sopravvento.
      // In browser puro: audio HTMLAudio + toast + Notification API + fetch
      // della lista pending per popolare il modal multi-sel.
      if (inWrapper) return;
      const bypassVolume = user?.bypassVolume === true;
      selectiveAlarm.start(bypassVolume);
      void refreshPending();
      const tipo = selective?.selectiveType ?? '?';
      toast.show(`🚨 Nuova selettiva: ${tipo}${selective?.zone ? ` - ${selective.zone}` : ''}`, 'error');
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('🚨 VVF - Nuova selettiva', {
          body: `${selective?.selectiveType ?? 'Allarme'}${selective?.zone ? ` · ${selective.zone}` : ''}`,
          requireInteraction: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
      }
    };

    // Boot iniziale: se l'utente apre la PWA durante una selettiva attiva,
    // mostra subito il modal con la lista corrente.
    if (!inWrapper && user?.id) void refreshPending();

    // "Intervento non piu' necessario": SUONA l'end-chime + modal "Ho ricevuto"
    // ai SI/Arrived, MA la selettiva RESTA attiva (NON la rimuovo dal modal
    // pending). L'invalidate aggiorna comunque il flag noLongerNeededAt.
    const handleSelectiveNoLongerNeeded = (payload: { selectiveId?: string; acceptedUserIds?: string[] }) => {
      qc.invalidateQueries({ queryKey: ['selectives.active'] });
      qc.invalidateQueries({ queryKey: ['dashboard.summary'] });
      // In wrapper: niente modal endChime PWA (c'e' EndChimeModal nativo).
      if (inWrapper) return;
      const myId = user?.id;
      if (myId && payload?.selectiveId && payload?.acceptedUserIds?.includes(myId)) {
        // Appendi alla coda: il modal mostra una per volta, ack rimuove
        // la prima. Idempotenza: non duplicare se gia' in coda.
        const sid = payload.selectiveId;
        setPendingEndChimes(prev => prev.includes(sid) ? prev : [...prev, sid]);
        selectiveAlarm.playEndChime();
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('🔕 VVF - Intervento non più necessario', {
            body: 'Potete rientrare. Tocca per confermare.',
            requireInteraction: true,
          });
        }
      }
    };

    // "Selettiva terminata": cleanup SILENZIOSO. Rimuove la selettiva dal modal
    // pending e ferma l'eventuale sirena in corso. NESSUN suono end-chime (a
    // differenza di "non piu' necessario"). Il GPS in wrapper lo ferma il nativo.
    const handleSelectiveTerminated = (payload: { selectiveId?: string; acceptedUserIds?: string[] }) => {
      qc.invalidateQueries({ queryKey: ['selectives.active'] });
      qc.invalidateQueries({ queryKey: ['dashboard.summary'] });
      // Se la selettiva era nel modal pending, rimuovila.
      if (payload?.selectiveId) {
        setPending(prev => prev.filter(s => s.selectiveId !== payload.selectiveId));
      }
      // Browser puro: ferma la sirena selettiva (no end-chime). In wrapper il
      // GPS/cleanup nativo e' gestito dal wrapper.
      if (inWrapper) return;
      selectiveAlarm.stop();
      // Browser puro: ferma il tracking GPS della selettiva terminata.
      if (payload?.selectiveId && gpsTracking.getActive() === payload.selectiveId) {
        gpsTracking.stop();
      }
    };

    conn.on('selectiveCreated', handleSelectiveCreated);
    conn.on('selectiveNoLongerNeeded', handleSelectiveNoLongerNeeded);
    conn.on('selectiveTerminated', handleSelectiveTerminated);

    // Listener per eventi dal wrapper nativo (modal selettiva accetta/rifiuta).
    // Quando l'utente preme "Accetto" sul modal nativo, il wrapper dispatcha
    // un CustomEvent 'vvf:native:accept' o 'vvf:native:reject' con selectiveId.
    const unsubNative = onNativeSelectiveResponse(async (selectiveId, accepted) => {
      // Acquisisco GPS solo se accetto. In wrapper nativo controllo prima
      // che il GPS hardware sia attivo: se no, propongo apertura settings.
      // Il foreground retry handler del wrapper riavviera' il task quando
      // l'utente torna in app (Problema 1).
      let lat: number | null = null, lng: number | null = null;
      if (accepted) {
        if (inWrapper) {
          const gpsOn = await checkNativeGpsEnabled();
          if (!gpsOn) {
            toast.show('GPS spento: attivalo nelle Impostazioni per tracciare il tragitto', 'error');
            openNativeGpsSettings();
          }
        }
        if (navigator.geolocation) {
          await new Promise<void>(resolve => {
            navigator.geolocation.getCurrentPosition(
              p => { lat = p.coords.latitude; lng = p.coords.longitude; resolve(); },
              () => resolve(),
              { timeout: 5000 },
            );
          });
        }
      }
      // POST /respond con gestione del 409 AlreadyActiveOnAnother: se l'utente
      // accetta una seconda selettiva mentre e' gia' attivo su un'altra, il
      // backend risponde 409. Chiediamo conferma allo switch (rifiuta vecchia
      // + accetta nuova) come fa SelectivesModals.tryAccept. Senza questo
      // handling il toast generico "Errore" ingannava l'utente facendogli
      // credere che ACCETTA fosse rotto, e di conseguenza terminate non
      // mandava end-chime per la selettiva mai realmente accettata.
      try {
        await api.post(`/selectives/${selectiveId}/respond`, {
          response: accepted ? 'Accepted' : 'Rejected',
          latitude: lat, longitude: lng,
        });
      } catch (err: unknown) {
        const e = err as { response?: { status?: number; data?: { error?: string; activeSelectiveId?: string; activeSelectiveType?: string; activeSelectiveZone?: string } } };
        if (accepted && e?.response?.status === 409 && e.response.data?.error === 'AlreadyActiveOnAnother') {
          const otherId = e.response.data.activeSelectiveId!;
          const otherType = e.response.data.activeSelectiveType ?? 'Altra';
          const otherZone = e.response.data.activeSelectiveZone;
          // Nel wrapper RN il flow accept/switch e' ormai 100% nativo (vedi
          // WebViewApp.handleAccept). Questo handler resta come safety net
          // se per qualche motivo arrivasse comunque l'evento — useConfirm
          // HTML basta perche' non c'e' piu' un modal nativo aperto.
          const ok = await confirm({
            title: '⚠️ Sei già attivo su un\'altra selettiva',
            message: `Sei attualmente attivo su "${otherType}"${otherZone ? ` in ${otherZone}` : ''}. Per accettare questa devi prima rifiutarla. Procedere?`,
            confirmLabel: 'Rifiuta e accetta questa',
            cancelLabel: 'Annulla',
            variant: 'danger',
          });
          if (!ok) return; // utente annulla: modal multi-sel resta aperto su questa
          try {
            await api.post(`/selectives/${otherId}/respond`, { response: 'Rejected', latitude: null, longitude: null });
          } catch {
            toast.show('Errore nel rifiutare l\'altra selettiva', 'error');
            return;
          }
          if (gpsTracking.getActive() === otherId) gpsTracking.stop();
          // Allinea anche il wrapper: rimuovi l'altra dal modal multi-sel
          // (se per qualche motivo era ancora in pending lista) marcandola
          // come gestita lato wrapper. notifyNativeSelectiveResponse e'
          // no-op in browser puro, quindi safe da chiamare sempre.
          notifyNativeSelectiveResponse(otherId, false);
          try {
            await api.post(`/selectives/${selectiveId}/respond`, {
              response: 'Accepted', latitude: lat, longitude: lng,
            });
          } catch {
            toast.show('Errore invio risposta selettiva', 'error');
            return;
          }
        } else {
          toast.show('Errore invio risposta selettiva', 'error');
          return;
        }
      }
      notifyNativeSelectiveResponse(selectiveId, accepted);
      // Avvio tracking continuativo (interval JS in PWA, foreground service
      // in wrapper). Anche se GPS spento ora, il wrapper retenta al ritorno
      // foreground (vedi AppState listener in WebViewApp).
      if (accepted) gpsTracking.start(selectiveId);
      if (!inWrapper) selectiveAlarm.stop();
      toast.show(accepted ? '✓ Hai accettato la selettiva' : '✗ Hai rifiutato la selettiva',
        accepted ? 'success' : 'info');
    });

    // Listener esito tracking GPS dal wrapper (audit #4). Mostra un toast
    // se startBackgroundGps fallisce per permessi negati, cosi' l'utente
    // sa che la posizione NON e' tracciata e puo' rimediare.
    const unsubGpsStatus = onNativeGpsTrackingStatus((active, reason) => {
      if (active) return; // tutto ok, nessun avviso
      const msg = reason === 'no-fg-permission'
        ? 'Permesso posizione negato: il tuo tragitto non verra tracciato. Concedi il permesso nelle Impostazioni.'
        : reason === 'no-bg-permission'
        ? 'Permesso "Posizione sempre" negato: il tracciamento si fermera quando l\'app va in background.'
        : 'Errore avvio tracciamento GPS. Il tuo tragitto potrebbe non essere visibile sulla mappa.';
      toast.show(msg, 'error');
    });

    return () => {
      conn.off('selectiveCreated', handleSelectiveCreated);
      conn.off('selectiveNoLongerNeeded', handleSelectiveNoLongerNeeded);
      conn.off('selectiveTerminated', handleSelectiveTerminated);
      unsubNative();
      unsubGpsStatus();
    };
  }, [toast, qc, navigate, user, confirm]);

  // Handler bottoni del modal multi-sel: delega l'accept/reject all'hook
  // useSelectiveResponseHandler (centralizza 409 + GPS + invalidate queries
  // condividendo logica con ActiveSelectiveItem).
  const respondToCurrent = async (accepted: boolean) => {
    const idx = Math.min(pendingIndex, pending.length - 1);
    const current = pending[idx];
    if (!current) return;
    const outcome = accepted
      ? await responseHandler.accept(current.selectiveId, async () => {
          if (!navigator.geolocation) return { lat: null, lng: null };
          return new Promise<{ lat: number | null; lng: number | null }>(resolve => {
            navigator.geolocation.getCurrentPosition(
              p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
              () => resolve({ lat: null, lng: null }),
              { timeout: 5000 },
            );
          });
        })
      : await responseHandler.reject(current.selectiveId);
    if (outcome === 'cancelled' || outcome === 'error') return;
    // Rimuovi la selettiva dalla coda locale + audio off se vuota.
    setPending(prev => {
      const next = prev.filter(s => s.selectiveId !== current.selectiveId);
      if (next.length === 0) selectiveAlarm.stop();
      return next;
    });
  };

  if (pendingEndChimes.length === 0 && pending.length === 0) return null;

  const currentEndChimeId = pendingEndChimes[0] ?? null;

  const ackEndChime = () => {
    const id = currentEndChimeId;
    if (!id) return;
    setPendingEndChimes(prev => {
      const next = prev.filter(s => s !== id);
      if (next.length === 0) selectiveAlarm.stopEndChime();
      return next;
    });
    // Ferma il repeater backend per QUESTA selettiva.
    api.post(`/selectives/${id}/ack-end-chime`).catch(() => { /* ignore */ });
  };

  return (
    <>
      {pending.length > 0 && (
        <SelectiveMultiModal
          pending={pending}
          pendingIndex={pendingIndex}
          setPendingIndex={setPendingIndex}
          respondToCurrent={respondToCurrent}
        />
      )}
      {currentEndChimeId !== null && (
        <SelectiveEndChimeModal count={pendingEndChimes.length} onAck={ackEndChime} />
      )}
    </>
  );
}

